import Card from "components/Card/Card";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import KaboodleToaster from "components/common/KaboodleToaster";
import AdvancedTextArea from "components/inputs/TextArea/AdvancedTextArea";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api";

type ClaimNotesFieldProps = {
  fileId: string | number | undefined | null;
  readOnly?: boolean;
  variant: "operations" | "future-medical";
  cardNumber?: number;
};

/**
 * Claim Notes for Ops + Future Medical.
 *
 * Local state + direct file PATCH. Not wired through ControlledTextArea or
 * parent-form resets — those prevented Future Medical saves from sticking.
 */
const ClaimNotesField = ({
  fileId,
  readOnly = false,
  variant,
  cardNumber = 4,
}: ClaimNotesFieldProps) => {
  const id = fileId != null ? String(fileId) : "";
  // Fetched independently of whatever the parent page already fetched
  // (e.g. ClaimantInformationForm/FMOperations both call this same query
  // for the same fileId). Intentional, not redundant network traffic: RTK
  // Query dedupes identical query args into one shared cache entry/request,
  // and this component needs to be able to fetch on its own regardless of
  // whether a given caller happens to already have fileData in scope.
  const { data: fileData } = useGetFileByIdQuery(
    { id },
    { skip: !id, refetchOnMountOrArgChange: true },
  );
  const [patchFileById] = usePatchFileByIdMutation();

  const serverClaimNotes = fileData?.claim_notes || "";
  const [value, setValue] = useState(serverClaimNotes);
  const valueRef = useRef(serverClaimNotes);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track which server value we've already applied, so a refetch with the
  // same notes doesn't fight the editor.
  const appliedServerValueRef = useRef(serverClaimNotes);
  // persist() needs to call schedulePersist() (see the in-flight-edit race
  // comment below), but schedulePersist() is defined after persist() and
  // itself depends on persist() -- a ref breaks the circular dependency.
  const schedulePersistRef = useRef<() => void>(() => {});

  const clearSaveTimer = useCallback(() => {
    if (saveTimerRef.current != null) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  const persist = useCallback(async () => {
    if (!id || readOnly || savingRef.current) return;
    if (!dirtyRef.current) return;

    const next = valueRef.current;
    savingRef.current = true;
    try {
      await patchFileById({
        id,
        data: { claim_notes: next },
        useDetail: true,
      }).unwrap();
      // valueRef may have moved on while this request was in flight (the
      // user kept typing). Only clear dirty if we actually saved the
      // latest value -- otherwise a later blur/debounce would see
      // dirtyRef === false and skip saving the newer text entirely,
      // leaving it visible locally but never persisted.
      if (valueRef.current === next) {
        dirtyRef.current = false;
      } else {
        schedulePersistRef.current();
      }
      appliedServerValueRef.current = next;
    } catch (error) {
      console.error("Failed to save claim_notes:", error);
      KaboodleToaster.error("Failed to save Claim Notes. Please try again.");
    } finally {
      savingRef.current = false;
    }
  }, [id, readOnly, patchFileById]);

  const schedulePersist = useCallback(() => {
    clearSaveTimer();
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void persist();
    }, 600);
  }, [clearSaveTimer, persist]);
  schedulePersistRef.current = schedulePersist;

  // Keep a stable persist ref for unmount flush so the effect doesn't
  // re-subscribe (and re-fire cleanup) every render.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  // If this flush's PATCH fails (e.g. slow/dropped network right as the
  // user navigates away), the error is still surfaced: KaboodleToaster is a
  // global toast utility decoupled from this component's lifecycle, not
  // React state, so persist()'s own catch/toast below fires normally even
  // though ClaimNotesField itself is already unmounted by then.
  useEffect(() => {
    return () => {
      clearSaveTimer();
      void persistRef.current();
    };
  }, [clearSaveTimer]);

  // Apply DB value when it arrives or changes (navigation / other screen).
  useEffect(() => {
    if (dirtyRef.current || savingRef.current) return;
    if (appliedServerValueRef.current === serverClaimNotes) return;
    appliedServerValueRef.current = serverClaimNotes;
    valueRef.current = serverClaimNotes;
    setValue(serverClaimNotes);
  }, [serverClaimNotes]);

  const handleChange = useCallback(
    (next: string) => {
      dirtyRef.current = true;
      valueRef.current = next;
      setValue(next);
      schedulePersist();
    },
    [schedulePersist],
  );

  const handleBlur = useCallback(() => {
    clearSaveTimer();
    void persist();
  }, [clearSaveTimer, persist]);

  const editor = (
    <div className="textarea textarea--advanced-mode">
      <label htmlFor="claim_notes">Claim Notes</label>
      <AdvancedTextArea
        name="claim_notes"
        value={value}
        errors={{}}
        onChange={handleChange}
        onBlur={handleBlur}
        isEditable={!readOnly}
      />
    </div>
  );

  if (variant === "future-medical") {
    return (
      <FutureMedicalCard heading="Claim Notes" cardNumber={cardNumber}>
        {editor}
      </FutureMedicalCard>
    );
  }

  // cardNumber must be passed here too, same as the future-medical branch
  // above -- Card.tsx builds its className as `card card-${cardNumber}`,
  // so omitting it doesn't just skip a class, it emits the literal
  // "card-undefined" class.
  return (
    <Card heading="Claim Notes" cardNumber={cardNumber}>
      {editor}
    </Card>
  );
};

export default ClaimNotesField;
